/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/jsx-props-no-spreading, camelcase */

import React, { useCallback, useEffect, useState } from 'react';
import { useConfig } from 'contexts/config';
import { get } from 'lodash';
import { Select, SelectItem, SelectItemGroup, TextInput } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { findFirstAppWithAccess } from 'utils/common';

function CredentialsSelector({ onChange = () => {}, allowCustom = false, onSelectorChange = () => {}, ...others }) {
  const config = useConfig();
  const { t } = useTranslation();
  const [appGroups, updateAppGroups] = useState({});
  const [appWithAccess, updateAppWithAccess] = useState(null);
  const [customCredentialSelected, setCustomCredentialSelected] = useState(false);
  const CUSTOM_CREDENTIAL = '_custom';

  useEffect(() => {
    const generateCredentialDescription = (cred, index) =>
      cred.description || cred.title || cred.name || t('credential_number', { credential_number: index + 1 });

    const generateAppTitle = (app, index) => app.title || app.name || t('app_number', { app_number: index + 1 });

    if (config.apps) {
      const credentials = config.apps.map((app, appIndex) => ({
        name: `${generateAppTitle(app, appIndex)}`,
        credentials: app.credentials.map((cred, index) => ({
          description: `${generateAppTitle(app, appIndex)} - ${generateCredentialDescription(cred, index)}`,
          client_id: cred.client_id
        })),

        subscribed: Object.prototype.hasOwnProperty.call(app, 'subscribed') ? app.subscribed : true
      }));

      credentials.sort((a, b) => !a.subscribed - !b.subscribed);

      updateAppGroups(credentials);
    }
  }, [config.apps, t]);

  useEffect(() => {
    updateAppWithAccess(findFirstAppWithAccess(appGroups));
  }, [appGroups]);

  const generateCredentialsPayload = (e) => {
    const credentialsPayload = {
      isCustomCredential: false
    };
    const { selectedIndex } = e.target;
    credentialsPayload.selectedApplication = get(e.target, `options[${selectedIndex}].parentNode.label`, null);
    credentialsPayload.selectedCredential = get(e.target, 'value', null);
    if (credentialsPayload.selectedCredential === CUSTOM_CREDENTIAL) {
      credentialsPayload.selectedCredential = '';
      credentialsPayload.isCustomCredential = true;
    }
    return credentialsPayload;
  };

  const onCredentialSelect = useCallback(
    (payload) => {
      if (payload.isCustom || get(payload, 'event.target.value') === CUSTOM_CREDENTIAL) {
        setCustomCredentialSelected(true);
        onChange(generateCredentialsPayload(payload.event));
      } else {
        setCustomCredentialSelected(false);
        onChange(generateCredentialsPayload(payload.event));
      }
    },
    [onChange]
  );

  const getDefaultValue = () => {
    if (appWithAccess) return appWithAccess.credentials.client_id;
    if (allowCustom) return CUSTOM_CREDENTIAL;
    return 'placeholder_credential';
  };

  if (appGroups.length > 0) {
    const singleCred = appGroups.flatMap((app) => app.credentials).length === 1;
    if (singleCred && !allowCustom && appWithAccess) {
      const { credentials } = appGroups[0];
      return <div className="singleCredential" data-testid="single-credential">{`${credentials[0].description}`}</div>;
    }
    return (
      <div className="credentialSelector">
        <Select
          hideLabel
          data-testid="credential-selector"
          id="credential-select"
          onChange={(e) => {
            onSelectorChange(e);
            onCredentialSelect({ isCustom: false, event: e });
          }}
          key={getDefaultValue()}
          defaultValue={getDefaultValue()}
          {...others}
        >
          {!appWithAccess && <SelectItem disabled hidden value="placeholder_credential" text={`---${t('app_no_access')}---`} />}
          {appGroups.map(({ name, credentials, subscribed }, index) => (
            <SelectItemGroup data-testid="credentials-group" key={`${name + index}`} label={name}>
              {credentials.map(({ description, client_id }) => (
                <SelectItem data-testid="credentials-item" key={client_id} value={client_id} text={description} disabled={!subscribed} />
              ))}
            </SelectItemGroup>
          ))}

          {allowCustom && (
            <SelectItemGroup data-testid="credentials-group" label={t('other')}>
              <SelectItem data-testid="credentials-item" value={CUSTOM_CREDENTIAL} text={t('other')} />
            </SelectItemGroup>
          )}
        </Select>
        {(customCredentialSelected || getDefaultValue() === CUSTOM_CREDENTIAL) && (
          <TextInput
            data-testid="custom-clientID"
            placeholder={t('enter_client_id')}
            className="custom-clientID"
            required
            id="client_id"
            name="clientId"
            labelText={t('client_id')}
            hideLabel
            onChange={(e) => {
              onCredentialSelect({ isCustom: true, event: e });
            }}
          />
        )}
      </div>
    );
  }
  if (allowCustom) {
    return (
      <TextInput
        placeholder={t('enter_client_id')}
        data-testid="client_id"
        required
        id="client_id"
        name="clientId"
        labelText={t('client_id')}
        hideLabel
        onChange={(e) => {
          onCredentialSelect({ isCustom: true, event: e });
        }}
      />
    );
  }
  return null;
}

CredentialsSelector.propTypes = {
  onChange: PropTypes.func,
  onSelectorChange: PropTypes.func,
  allowCustom: PropTypes.bool
};

export default CredentialsSelector;
